import React, { useState } from 'react';
import { formatDublinDateTime } from '../../utils/date';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { useGetMessageAuditQuery, useWithdrawMessageMutation } from '../../store/api/localApi';
import ConfirmDialog from '../../components/ConfirmDialog';
import { MessageStatus, MessageDeliveryStatus, MessageTargetType } from '../../types';
import { useToast } from '../../components/Toast';
import { DeliveryStatusBadge, PushStatusBadge } from '../../components/delivery/DeliveryBadges';
import MessageRepliesList from './MessageRepliesList';

// ── MessageDetailPage ────────────────────────────────────────────────────────
//
// Shows full message detail + per-recipient audit table for sent messages.
// Allows an authorised sender (or admin) to withdraw the message if no
// recipient has read it yet.

function formatDateTime(iso: string): string {
  return formatDublinDateTime(iso);
}

function targetDescription(
  targetType: MessageTargetType,
  targetOrgId?: string,
  targetLocationId?: string,
  targetClassId?: string,
  targetStudentIds?: string[]
): string {
  switch (targetType) {
    case MessageTargetType.ORG:
      return `Entire organisation${targetOrgId ? ` (${targetOrgId})` : ''}`;
    case MessageTargetType.LOCATION:
      return `Location${targetLocationId ? ` (${targetLocationId})` : ''}`;
    case MessageTargetType.CLASS:
      return `Class${targetClassId ? ` (${targetClassId})` : ''}`;
    case MessageTargetType.STUDENTS:
      return `${targetStudentIds?.length ?? 0} specific student${
        (targetStudentIds?.length ?? 0) === 1 ? '' : 's'
      }`;
    case MessageTargetType.STUDENT_PARENTS:
      return `Student parents${
        targetStudentIds?.length
          ? ` (${targetStudentIds.length} student${targetStudentIds.length === 1 ? '' : 's'})`
          : ''
      }`;
    default:
      return targetType;
  }
}

function StatusBadge({ status }: { status: MessageStatus }) {
  const colours =
    status === MessageStatus.WITHDRAWN
      ? 'bg-gray-100 text-gray-600'
      : 'bg-green-100 text-green-700';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colours}`}
    >
      {status === MessageStatus.WITHDRAWN ? 'Withdrawn' : 'Sent'}
    </span>
  );
}

const MessageDetailPage: React.FC = () => {
  const { messageId } = useParams<{ messageId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();

  const { data, isLoading, error } = useGetMessageAuditQuery(messageId ?? '', { skip: !messageId });

  const [withdrawMessage, { isLoading: isWithdrawing }] = useWithdrawMessageMutation();
  const [showConfirm, setShowConfirm] = useState(false);

  // navigate(-1) goes back in app history; for deep links (push notification,
  // bookmark, refresh) there is no history entry to pop, so we fall back to
  // the parent Messages list (one path segment up). The Toast provider sits
  // above the router, so success toasts survive either navigation.
  const goBack = React.useCallback(() => {
    // react-router exposes a numeric idx on location.state since v6; if
    // it's 0 (or absent) we're on the first entry of this session.
    const idx = (location.state as { idx?: number } | null)?.idx;
    if (idx === undefined || idx === 0) {
      navigate('..', { relative: 'path' });
    } else {
      navigate(-1);
    }
  }, [navigate, location.state]);

  if (!messageId) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-gray-500">No message ID provided.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-12 text-center" data-testid="messages-detail-page-loading">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <p className="mt-2 text-sm text-gray-500">Loading message…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-12 text-center" data-testid="messages-detail-page-error">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Failed to load message</h3>
        <p className="mt-1 text-sm text-gray-500">Please try again later.</p>
        <button
          type="button"
          onClick={goBack}
          className="mt-4 text-sm text-primary-600 hover:text-primary-700"
        >
          Go back
        </button>
      </div>
    );
  }

  const { message, deliveries } = data;

  const readCount = deliveries.filter((d) => d.status === MessageDeliveryStatus.READ).length;
  // Withdraw is sender-only on the backend. Hide the control entirely for
  // viewers (org-admins, location-admins, super-admins) who can see the
  // audit but cannot withdraw — UI hides, backend enforces.
  const isSender = !!user && message.senderId === user.id;
  const canWithdraw = isSender && message.status === MessageStatus.SENT && readCount === 0;
  const alreadyRead = isSender && message.status === MessageStatus.SENT && readCount > 0;

  const handleWithdraw = () => setShowConfirm(true);

  const handleConfirmWithdraw = async () => {
    setShowConfirm(false);
    try {
      await withdrawMessage(messageId).unwrap();
      showSuccess('Message withdrawn');
      goBack();
    } catch (err: unknown) {
      const apiError = err as {
        status?: number;
        data?: { error?: string; firstReadAt?: string };
      };
      if (apiError?.status === 409 || apiError?.data?.error === 'ALREADY_READ') {
        const readAt = apiError?.data?.firstReadAt
          ? formatDateTime(apiError.data.firstReadAt)
          : 'an unknown time';
        showError(`Can't withdraw — a recipient read this at ${readAt}`);
      } else {
        showError('Failed to withdraw message. Please try again.');
      }
    }
  };

  return (
    <>
      <div className="py-6" data-testid={`messages-detail-${messageId}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {/* Back link */}
          <button
            type="button"
            data-testid="messages-detail-back"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>

          {/* Message header card */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1
                      className="text-lg font-bold text-gray-900 truncate"
                      data-testid="messages-detail-title"
                    >
                      {message.title}
                    </h1>
                    <StatusBadge status={message.status} />
                  </div>
                  <dl className="mt-2 grid grid-cols-1 gap-y-1 sm:grid-cols-2 text-sm text-gray-600">
                    <div>
                      <dt className="inline font-medium text-gray-700">From: </dt>
                      <dd className="inline" data-testid="messages-detail-sender">
                        {message.senderName}{' '}
                        <span className="text-gray-400">({message.senderRole})</span>
                      </dd>
                    </div>
                    <div>
                      <dt className="inline font-medium text-gray-700">To: </dt>
                      <dd className="inline" data-testid="messages-detail-target">
                        {targetDescription(
                          message.targetType,
                          message.targetOrgId,
                          message.targetLocationId,
                          message.targetClassId,
                          message.targetStudentIds
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="inline font-medium text-gray-700">Sent: </dt>
                      <dd className="inline" data-testid="messages-detail-sent-at">
                        {formatDateTime(message.createdAt)}
                      </dd>
                    </div>
                    <div>
                      <dt className="inline font-medium text-gray-700">Read: </dt>
                      <dd className="inline" data-testid="messages-detail-read-count">
                        {readCount} / {message.recipientCount}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Withdraw controls */}
                <div className="flex-shrink-0">
                  {canWithdraw && (
                    <button
                      type="button"
                      data-testid={`messages-withdraw-btn-${messageId}`}
                      onClick={handleWithdraw}
                      disabled={isWithdrawing}
                      className="inline-flex items-center gap-2 rounded-md bg-red-600 px-3.5 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isWithdrawing ? 'Withdrawing…' : 'Withdraw message'}
                    </button>
                  )}
                  {alreadyRead && (
                    <button
                      type="button"
                      // Use a real disabled button so toBeDisabled() works and
                      // the testid is unambiguous (no duplicate with the enabled
                      // variant above — that one only renders when canWithdraw).
                      disabled
                      data-testid={`messages-withdraw-disabled-${messageId}`}
                      title={`Can't withdraw — ${readCount} parent${
                        readCount === 1 ? ' has' : 's have'
                      } already read this`}
                      className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3.5 py-2 text-sm text-gray-500 cursor-not-allowed"
                    >
                      <span>Withdraw</span>
                      <span className="text-xs text-red-500">({readCount} already read)</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Message body */}
            <div className="border-t border-gray-100 px-6 py-5">
              <p
                className="text-sm text-gray-800 whitespace-pre-wrap"
                data-testid="messages-detail-body"
              >
                {message.body}
              </p>
            </div>

            {/* Withdrawn info */}
            {message.status === MessageStatus.WITHDRAWN && message.withdrawnAt && (
              <div className="border-t border-gray-100 px-6 py-3 bg-gray-50">
                <p className="text-xs text-gray-500" data-testid="messages-detail-withdrawn-info">
                  Withdrawn on {formatDateTime(message.withdrawnAt)}
                  {message.withdrawnBy ? ` by ${message.withdrawnBy}` : ''}.
                </p>
              </div>
            )}
          </div>

          {/* Per-recipient audit table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">
                Delivery audit ({deliveries.length})
              </h2>
            </div>

            {deliveries.length === 0 ? (
              <p className="px-6 py-8 text-sm text-gray-500 text-center">
                No delivery records found.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table
                  className="min-w-full divide-y divide-gray-200 text-sm"
                  data-testid="messages-audit-table"
                >
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recipient ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Read at
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Push
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {deliveries.map((delivery) => (
                      <tr
                        key={delivery.id}
                        data-testid={`messages-audit-row-${delivery.id}`}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-4 py-3 font-mono text-xs text-gray-700 max-w-xs truncate">
                          {delivery.recipientUserId}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{delivery.recipientRole}</td>
                        <td className="px-4 py-3">
                          <DeliveryStatusBadge status={delivery.status} />
                        </td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {delivery.readAt ? formatDateTime(delivery.readAt) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <PushStatusBadge status={delivery.pushStatus} />
                          {delivery.pushError && (
                            <p
                              className="mt-0.5 text-xs text-red-500 truncate max-w-xs"
                              title={delivery.pushError}
                            >
                              {delivery.pushError}
                            </p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {message.repliesAllowed && (
            <MessageRepliesList
              messageId={message.id}
              alwaysShowLocation={message.targetType === MessageTargetType.ORG}
            />
          )}

          {/* Hidden success marker — appears after withdraw triggers navigation away.
            Placed here so a test can catch it if navigation is slow. */}
          {/* data-testid="messages-withdraw-success" is rendered by the Toast layer */}
        </div>
      </div>
      <ConfirmDialog
        isOpen={showConfirm}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirmWithdraw}
        title="Withdraw message?"
        message={`This will remove the message from all ${message.recipientCount} recipient inbox${
          message.recipientCount === 1 ? '' : 'es'
        }.`}
        confirmLabel="Withdraw"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={isWithdrawing}
      />
    </>
  );
};

export default MessageDetailPage;
